<template>
  <div :class="$style.content">
    <ValidationObserver
      v-slot="{ invalid }"
    >
      <SeverityModal
        v-if="showApnsSaveModal"
        :notificationMessage="severityModalData.notificationMessage"
        :notificationTitle="severityModalData.notificationTitle"
        :primaryButtonProps="severityModalData.primaryButtonProps"
        :showModal="showApnsSaveModal"
        title="Configure APNs Certificate"
        variant="sev2"
        @close="showApnsSaveModal = false;"
        @save="handleSave"
      >
        <template v-slot:subtitle>
          <ParagraphBody
            :data-test-id="$testId('subtitle')"
            weight="semibold"
          >
            Are you sure you want to configure the APNs certificate with {{ appleId }}?
          </ParagraphBody>
        </template>
        <template v-slot:learnmore>
          <ParagraphBody>
            <span>For more information, see </span>
            <Link
              :href="severityModalData.kbLink"
              target="_blank"
              :text="severityModalData.kbText"
            />
          </ParagraphBody>
        </template>
      </SeverityModal>
      <ButtonBack
        @click="handleBackButton"
      />
      <h1 :class="$style.title">
        {{ titleHeader }}
      </h1>
      <p>{{ title.body }}</p>
      <div v-if="!isVpp">
        <p>{{ checklistGreeting }}</p>
        <div :class="$style.checklist">
          <ul
            v-for="(item, index) in checklistItems"
            :key="index"
          >
            <li>
              <ExternalLink
                v-if="item.linkUrl"
                :href="item.linkUrl"
                :text="item.linkText"
                @click="triggerChecklistItemClickedEvent(item.linkText)"
              />
              <span v-else>
                {{ item.linkText }}
              </span>
            </li>
          </ul>
        </div>
        <p>
          {{ checklistAlternative.body }}
          <span
            :class="$style.redirect"
            @click="handleOnClickRedirect"
          >
            {{ checklistAlternative.linkText }}
          </span>
        </p>
      </div>
      <ol :class="$style.list">
        <li
          v-if="steps.download"
          :class="$style.listItem"
        >
          <StepWizard
            :stepNumber="steps.download.stepNumber"
            :stepTitle="steps.download.title"
          >
            <p>
              {{ steps.download.text }}
            </p>
            <IconButton
              :data-test-id="$testId('downloadCsr')"
              :hasNoBackground="false"
              :hasOutline="true"
              icon="fileDownload"
              :text="steps.download.buttonText"
              type="secondary"
              @click="handleDownload"
            />
          </StepWizard>
        </li>
        <li
          v-if="steps.signIn"
          :class="$style.listItem"
        >
          <StepWizard
            :stepNumber="steps.signIn.stepNumber"
            :stepTitle="steps.signIn.title"
          >
            <p>
              <MarkdownInline
                data-test-id="mdmStepTwo"
                :mdTemplate="steps.signIn.text"
              />
            </p>
            <IconButton
              v-if="steps.signIn.buttonText"
              :data-test-id="$testId('goToAppleButton')"
              :hasNoBackground="false"
              :hasOutline="true"
              icon="externalLink"
              iconPosition="right"
              :isDisabled="certDisableSecondStep"
              :scale=".75"
              :text="steps.signIn.buttonText"
              type="secondary"
              @click="handleGoToApple"
            />
          </StepWizard>
        </li>
        <li
          v-if="steps.appleId && isCert"
          :class="$style.listItem"
        >
          <StepWizard
            :stepNumber="steps.appleId.stepNumber"
            :stepTitle="steps.appleId.title"
          >
            <p>
              <MarkdownInline
                data-test-id="mdmStepTwo"
                :mdTemplate="steps.appleId.text"
              />
            </p>
            <InputText
              v-model="appleId"
              ariaLabel="Apple Id"
              :class="$style.appleIdInput"
              :data-test-id="$testId('appleId')"
              :isDisabled="certDisableThirdStep"
              label="Enter Your Company Apple ID"
              name="Apple ID"
              rules="required|email"
            />
          </StepWizard>
        </li>
        <li
          v-if="steps.upload"
          :class="$style.listItem"
        >
          <StepWizard
            :data-test-id="$testId('fileUploader')"
            :stepNumber="steps.upload.stepNumber"
            :stepTitle="steps.upload.title"
          >
            <p>
              <MarkdownInline :mdTemplate="steps.upload.text" />
            </p>
            <FileUploader
              :accept="steps.upload.fileExtensions"
              :isDisabled="certDisableFourthStep || invalid"
              :labelConfig="fileUploadLabel"
              :loading="false"
              @fileRemoved="onRemovedFile"
              @fileUpload="onClickUploadFile"
            />
          </StepWizard>
        </li>
      </ol>
      <div :class="$style.buttonContainer">
        <FlatActionButton
          :action="showSaveModal"
          :buttonClass="['btnDefault', 'btnCTA']"
          data-test-id="saveBtn"
          :disabled="disabledBtn || invalid"
          :text="cta.text"
        />
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import {
  ButtonBack,
  FileUploader,
  IconButton,
  InputText,
  Link,
  MarkdownInline,
  ParagraphBody,
  SeverityModal,
  Types,
  addValidationRule,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import Constants, {
  ItemTypeConsts,
  MdmFormsConsts,
  MdmFormsTypes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import DownloadUtil from 'client/util/DownloadUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ExternalLink from 'components/ExternalLink.vue';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import StepWizard from 'client/MDM/components/StepWizard.vue';
import Util from 'client/util/Util';

const { openUrl } = Util;
const { PrimaryButtonProps } = Types;

const { error, success } = Constants;
const {
  certSetup,
  certRenew,
  depSetup,
  depRenew,
  vppToken,
  vppRenew,
} = MdmFormsTypes;

const mdmSettingsModel = StoreModelMap[ItemTypeConsts.mdmSettings];
const vppModule = StoreModuleMap[ItemTypeConsts.mdmVpp];

const checklistContent = {
  alternative: {
    body: 'If you don’t have access to these resources, or don’t need the capabilities offered by Apple MDM, use our proprietary protocol to manage your mac devices in the ',
    linkText: 'JumpCloud console.',
    linkRedirect: '#/devices/new',
  },
  greeting: 'Before you get started with MDM, here’s what you’ll need:',
  items: [
    {
      linkText: 'Role-based apple ID (i.e. itadmin@example.com)',
      linkUrl: 'https://support.apple.com/guide/apple-business-manager/what-are-managed-apple-ids-tes78b477c81/web',
    },
    {
      linkText: 'ABM Account',
      linkUrl: 'https://business.apple.com/',
    },
    {
      linkText: 'Apple Push Notifications Service Certificate (APNS Cert.)',
      linkUrl: KbArticleUrls.settingUpMdm,
    },
    {
      linkText: 'A Mac device, or a virtual machine',
    },
  ],
};

export default {
  name: 'MdmSetupForm',

  components: {
    ButtonBack,
    ExternalLink,
    FileUploader,
    FlatActionButton,
    IconButton,
    InputText,
    Link,
    MarkdownInline,
    ParagraphBody,
    SeverityModal,
    StepWizard,
    ValidationObserver,
  },

  openUrl,

  props: {
    /**
     * Device Manager ID
     */
    dmId: {
      type: String,
      default: '',
    },
    itemType: {
      type: String,
      required: true,
    },
    token: {
      type: Object,
      default: undefined,
    },
  },

  data: () => ({
    appleId: '',
    disabledBtn: true,
    certDisableSecondStep: false,
    certDisableThirdStep: false,
    certDisableFourthStep: false,
    fileUploadLabel: {
      fileLabel: 'File uploaded',
      removeButton: 'Remove file',
      noFile: 'No file uploaded',
      uploadButton: 'Choose a file',
      uploadLabel: 'Drop files here',
      uploadFailed: 'File upload failed',
    },
    severityModalData: {
      kbLink: KbArticleUrls.mdmManageDevices,
      kbText: 'Manage Apple Devices.',
      notificationMessage: 'The email address associated with the APNs certificate can only be updated or changed at renewal. Please verify the email address has been entered correctly.',
      notificationTitle: 'APNs Certificate Email Address',
      primaryButtonProps: PrimaryButtonProps({
        text: 'Complete Setup',
      }),
    },
    showApnsSaveModal: false,
    uploadedFile: '',
  }),

  computed: {
    ...mapGetters(mdmSettingsModel, [
      'getCreatorAppleId',
    ]),
    checklistAlternative() {
      return checklistContent.alternative;
    },
    checklistGreeting() {
      return checklistContent.greeting;
    },
    checklistItems() {
      return checklistContent.items;
    },
    /**
     * The computed properties title(), steps() and cta() reference the main structure of
     * the forms used in MDM. The contents of the forms are stored in MdmFormsConstants.js
     * To add more forms of this kind, go to that file and add the necessary copy there.
     */
    cta() {
      return MdmFormsConsts[this.itemType].cta;
    },
    isCert() {
      return this.itemType === certSetup || this.itemType === certRenew;
    },
    isVpp() {
      return this.itemType === vppToken || this.isVppRenew;
    },
    isVppRenew() {
      return this.itemType === vppRenew;
    },
    steps() {
      return MdmFormsConsts[this.itemType].steps;
    },
    title() {
      return MdmFormsConsts[this.itemType].title;
    },
    titleHeader() {
      return this.isVppRenew ? `${this.title.header} for ${this.token.locationName}` : this.title.header;
    },
  },

  mounted() {
    this.appleId = this.getCreatorAppleId;
    if (this.isCert) {
      this.disableInputs();
    }

    addValidationRule('required', { message: 'Enter the Apple ID that your company uses to manage your Apple devices.' });
    addValidationRule('email', { message: 'Your company Apple ID must be a valid email address.' });
  },

  methods: {
    ...mapActions(mdmSettingsModel, [
      'fetchCSRCertificate',
      'fetchDEPCertificate',
      'updateDep',
    ]),

    ...mapActions(vppModule, [
      'uploadSToken',
      'renewToken',
    ]),

    disableInputs() {
      this.certDisableSecondStep = true;
      this.certDisableThirdStep = true;
      this.certDisableFourthStep = true;
    },

    handleBackButton() {
      this.$emit('back-button');
    },

    handleDownload() {
      this.triggerButtonClickedEvent('Download button');
      if (this.isCert) {
        // second step is now enabled regardless of what user does with the dialog from step one
        this.certDisableSecondStep = false;
        this.fetchCSRCertificate(this.dmId)
          .then(resp => DownloadUtil.downloadFile(resp, 'download-mdm.csr'))
          .catch(() => {
            notify(error, 'An error occurred while attempting to retrieve the CSR file.');
          });
      } else if (this.itemType === depSetup || this.itemType === depRenew) {
        this.fetchDEPCertificate(this.dmId)
          .then(resp => DownloadUtil.downloadFile(resp, 'jumpcloud-dep.pem'))
          .catch(() => {
            notify(error, 'An error occurred while attempting to retrieve the PEM file.');
          });
      }
    },

    handleGoToApple() {
      // third step is now enabled regardless of what user does with the apple link from step two
      this.certDisableThirdStep = false;
      this.certDisableFourthStep = false;
      this.$options.openUrl('https://identity.apple.com/pushcert');
    },

    handleOnClickRedirect() {
      this.triggerChecklistItemClickedEvent('JumpCloud console devices redirect');
      Util.redirect(this.checklistAlternative.linkRedirect);
    },

    handleSave() {
      this.triggerButtonClickedEvent('Complete Setup button');
      if (this.uploadedFile) {
        try {
          if (this.isVpp) {
            const payload = { sToken: this.uploadedFile };
            if (this.isVppRenew) payload.tokenId = this.token.id;
            const action = this.itemType === vppToken
              ? this.uploadSToken
              : this.renewToken;

            action(payload)
              .then(this.notifySetupCompleted)
              .catch(this.notifyError);
          } else {
            const payload = this.isCert
              ? { appleSignedCert: btoa(this.uploadedFile), appleCertCreatorAppleID: this.appleId }
              : { encryptedDepServerToken: btoa(this.uploadedFile) };

            this.updateDep(payload)
              .then(this.notifySetupCompleted)
              .catch(this.notifyError);
          }
        } catch (e) {
          notify(error, 'An error occurred while trying to upload the file.');
        }
      }
    },

    notifyError(err) {
      const message = `<strong>Unable to upload file!</strong><br> ${get(err, 'response.data.message', 'An error occurred while trying to upload the file.')}`;
      notify(error, message);
    },

    notifySetupCompleted() {
      notify(success, this.cta.notification);
      this.$emit('setup-completed');
    },

    async onClickUploadFile(fileInformation) {
      if (fileInformation.size) {
        try {
          if (fileInformation.size > 4096) {
            throw new Error('File is too large');
          }
          this.uploadedFile = await this.readUploadFileAsData(fileInformation);
          this.disabledBtn = false;
          this.$emit('file-uploaded', true);
        } catch (e) {
          notify(error, `The file could not be read ${e}`);
        }
      }
    },

    onRemovedFile() {
      this.disabledBtn = true;
      this.$emit('file-uploaded', false);
    },

    readUploadFileAsData(file) {
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onerror = () => {
          reader.abort();
          reject(new DOMException('There was a problem parsing the input file.'));
        };

        reader.onload = () => {
          resolve(reader.result);
        };

        reader.readAsText(file);
      });
    },

    showSaveModal() {
      if (this.isCert) {
        this.showApnsSaveModal = true;
      } else {
        this.handleSave();
      }
    },

    triggerButtonClickedEvent(section) {
      EventHelper.triggerMdmButtonClicked({
        section,
      });
    },

    triggerChecklistItemClickedEvent(content) {
      EventHelper.triggerMdmChecklistItemClicked({ content });
    },
  },
};
</script>

<style module>
@import './css/certificateStyles.css';

@value (white, gradientLight4) from '~css/colors.css';

.appleIdInput {
  max-width: 350px;
}

.checklist {
  margin-bottom: 20px;
  margin-top: 20px;
}

.linkIcon {
  margin-left: 10px;
}

.list {
  counter-reset: list-counter;
  list-style-type: none;
}

.listItem {
  background-color: white;
  box-shadow: 0 0 8px 0.5px gradientLight4;
  margin-bottom: 10px;
  padding: 15px;
  position: relative;
}

.fileContainer {
  height: 150px;
}

.redirect {
  color: var(--jc-color-informational-base);
  cursor: pointer;
}
</style>
